/**
 * Черновик для ЕГЭ №1 (граф + таблица) и №4 (дерево + таблица).
 * Режим tree — иерархическая раскладка; имена строк/столбцов редактируются.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { emptyGraphTableState, useWorkspaceSync } from './useWorkspaceSync';

function uid(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function buildInitialFromConfig(kind, config) {
  const mode = kind === 'tree_table' ? 'tree' : 'graph';
  const base = emptyGraphTableState(mode);
  if (!config || typeof config !== 'object') return base;
  return {
    ...base,
    ...config,
    mode: config.mode === 'tree' || kind === 'tree_table' ? 'tree' : 'graph',
    nodes: Array.isArray(config.nodes) ? config.nodes : [],
    edges: Array.isArray(config.edges) ? config.edges : [],
    rowLabels: Array.isArray(config.rowLabels) ? config.rowLabels : [],
    colLabels: Array.isArray(config.colLabels) ? config.colLabels : [],
    cells: config.cells && typeof config.cells === 'object' ? config.cells : {},
  };
}

function layoutTree(nodes, edges) {
  if (!nodes.length) return nodes;
  const children = new Map();
  const hasParent = new Set();
  for (const e of edges) {
    if (!children.has(e.from)) children.set(e.from, []);
    children.get(e.from).push(e.to);
    hasParent.add(e.to);
  }
  const roots = nodes.filter((n) => !hasParent.has(n.id));
  const rootsUse = roots.length ? roots : [nodes[0]];
  const positions = new Map();
  let leafX = 0;

  function place(id, depth) {
    const kids = (children.get(id) || []).filter((cid) => nodes.some((n) => n.id === cid));
    if (!kids.length) {
      positions.set(id, { x: 80 + leafX * 100, y: 40 + depth * 90 });
      leafX += 1;
      return;
    }
    kids.forEach((cid) => place(cid, depth + 1));
    const xs = kids.map((cid) => positions.get(cid)?.x).filter((x) => x != null);
    const x = xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 80 + leafX * 100;
    positions.set(id, { x, y: 40 + depth * 90 });
  }

  rootsUse.forEach((r) => place(r.id, 0));
  for (const n of nodes) {
    if (!positions.has(n.id)) {
      positions.set(n.id, { x: 80 + leafX * 100, y: 40 });
      leafX += 1;
    }
  }
  return nodes.map((n) => {
    const p = positions.get(n.id);
    return p ? { ...n, x: p.x, y: p.y } : n;
  });
}

export default function GraphTableWorkspace({
  kind = 'graph_table',
  ydoc,
  workspaceConfig,
  seedState,
  dataKey = 'data',
  onStateChange,
  readOnly = false,
}) {
  const initial = useMemo(
    () => buildInitialFromConfig(kind, workspaceConfig),
    [kind, workspaceConfig],
  );
  const [state, setState] = useWorkspaceSync({
    ydoc,
    initialState: initial,
    seedState,
    dataKey,
    onChange: onStateChange,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [linkFrom, setLinkFrom] = useState(null);
  const [dragId, setDragId] = useState(null);
  const svgRef = useRef(null);
  const isTree = state.mode === 'tree' || kind === 'tree_table';

  const nodesById = useMemo(() => {
    const m = new Map();
    (state.nodes || []).forEach((n) => m.set(n.id, n));
    return m;
  }, [state.nodes]);

  const addNode = useCallback(() => {
    if (readOnly) return;
    const id = uid('n');
    const label = String((state.nodes?.length || 0) + 1);
    setState((prev) => ({
      ...prev,
      nodes: [
        ...(prev.nodes || []),
        {
          id,
          label,
          x: 80 + ((prev.nodes?.length || 0) % 5) * 90,
          y: 60 + Math.floor((prev.nodes?.length || 0) / 5) * 80,
        },
      ],
    }));
    setSelectedId(id);
  }, [readOnly, setState, state.nodes?.length]);

  const removeSelected = useCallback(() => {
    if (readOnly || !selectedId) return;
    setState((prev) => ({
      ...prev,
      nodes: (prev.nodes || []).filter((n) => n.id !== selectedId),
      edges: (prev.edges || []).filter((e) => e.from !== selectedId && e.to !== selectedId),
    }));
    setSelectedId(null);
    setLinkFrom(null);
  }, [readOnly, selectedId, setState]);

  const renameSelected = useCallback(
    (label) => {
      if (readOnly || !selectedId) return;
      setState((prev) => ({
        ...prev,
        nodes: (prev.nodes || []).map((n) => (n.id === selectedId ? { ...n, label } : n)),
      }));
    },
    [readOnly, selectedId, setState],
  );

  const onNodeClick = useCallback(
    (id, ev) => {
      ev.stopPropagation();
      if (readOnly) {
        setSelectedId(id);
        return;
      }
      if (linkFrom && linkFrom !== id) {
        const edgeId = uid('e');
        setState((prev) => ({
          ...prev,
          edges: [...(prev.edges || []), { id: edgeId, from: linkFrom, to: id, weight: '' }],
        }));
        setLinkFrom(null);
        setSelectedId(id);
        return;
      }
      setSelectedId(id);
    },
    [linkFrom, readOnly, setState],
  );

  const onPointerDownNode = useCallback(
    (id, ev) => {
      if (readOnly || ev.button !== 0) return;
      ev.preventDefault();
      setDragId(id);
      setSelectedId(id);
    },
    [readOnly],
  );

  const onPointerMove = useCallback(
    (ev) => {
      if (!dragId || readOnly || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;
      setState((prev) => ({
        ...prev,
        nodes: (prev.nodes || []).map((n) => (n.id === dragId ? { ...n, x, y } : n)),
      }));
    },
    [dragId, readOnly, setState],
  );

  const onPointerUp = useCallback(() => setDragId(null), []);

  const autoLayout = useCallback(() => {
    if (readOnly) return;
    setState((prev) => ({
      ...prev,
      nodes: layoutTree(prev.nodes || [], prev.edges || []),
    }));
  }, [readOnly, setState]);

  const ensureTableSize = useCallback(
    (rows, cols) => {
      setState((prev) => {
        const rowLabels = [...(prev.rowLabels || [])];
        const colLabels = [...(prev.colLabels || [])];
        while (rowLabels.length < rows) rowLabels.push('');
        while (colLabels.length < cols) colLabels.push('');
        return {
          ...prev,
          rowLabels: rowLabels.slice(0, Math.max(rows, rowLabels.length)),
          colLabels: colLabels.slice(0, Math.max(cols, colLabels.length)),
        };
      });
    },
    [setState],
  );

  const setRowLabel = (i, value) => {
    if (readOnly) return;
    setState((prev) => {
      const rowLabels = [...(prev.rowLabels || [])];
      while (rowLabels.length <= i) rowLabels.push('');
      rowLabels[i] = value;
      return { ...prev, rowLabels };
    });
  };

  const setColLabel = (i, value) => {
    if (readOnly) return;
    setState((prev) => {
      const colLabels = [...(prev.colLabels || [])];
      while (colLabels.length <= i) colLabels.push('');
      colLabels[i] = value;
      return { ...prev, colLabels };
    });
  };

  const setCell = (r, c, value) => {
    if (readOnly) return;
    setState((prev) => ({
      ...prev,
      cells: { ...(prev.cells || {}), [`${r},${c}`]: value },
    }));
  };

  const setEdgeWeight = (edgeId, weight) => {
    if (readOnly) return;
    setState((prev) => ({
      ...prev,
      edges: (prev.edges || []).map((e) => (e.id === edgeId ? { ...e, weight } : e)),
    }));
  };

  const removeEdge = (edgeId) => {
    if (readOnly) return;
    setState((prev) => ({
      ...prev,
      edges: (prev.edges || []).filter((e) => e.id !== edgeId),
    }));
  };

  const rowCount = Math.max(3, state.rowLabels?.length || 0, 1);
  const colCount = Math.max(3, state.colLabels?.length || 0, 1);
  const selected = nodesById.get(selectedId);

  return (
    <div className={`graph-table-workspace ${isTree ? 'is-tree' : 'is-graph'}`}>
      <div className="gtw-toolbar">
        <button type="button" className="btn btn-outline btn-sm" onClick={addNode} disabled={readOnly}>
          + Узел
        </button>
        <button
          type="button"
          className={`btn btn-outline btn-sm ${linkFrom ? 'active' : ''}`}
          onClick={() => setLinkFrom((v) => (v ? null : selectedId))}
          disabled={readOnly || !selectedId}
          title="Клик по второму узлу создаст ребро"
        >
          {linkFrom ? 'Выберите второй узел…' : 'Связать'}
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={removeSelected} disabled={readOnly || !selectedId}>
          Удалить узел
        </button>
        {isTree ? (
          <button type="button" className="btn btn-outline btn-sm" onClick={autoLayout} disabled={readOnly}>
            Разложить дерево
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => ensureTableSize(rowCount + 1, colCount)}
          disabled={readOnly}
        >
          + Строка таблицы
        </button>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => ensureTableSize(rowCount, colCount + 1)}
          disabled={readOnly}
        >
          + Столбец
        </button>
      </div>

      <div className="gtw-body">
        <div className="gtw-canvas-wrap">
          <svg
            ref={svgRef}
            className="gtw-canvas"
            width="100%"
            height="100%"
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onClick={() => { setSelectedId(null); setLinkFrom(null); }}
          >
            {(state.edges || []).map((e) => {
              const a = nodesById.get(e.from);
              const b = nodesById.get(e.to);
              if (!a || !b) return null;
              const mx = (a.x + b.x) / 2;
              const my = (a.y + b.y) / 2;
              return (
                <g key={e.id}>
                  <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} className="gtw-edge" />
                  {e.weight ? (
                    <text x={mx} y={my - 6} className="gtw-edge-label" textAnchor="middle">
                      {e.weight}
                    </text>
                  ) : null}
                </g>
              );
            })}
            {(state.nodes || []).map((n) => (
              <g
                key={n.id}
                transform={`translate(${n.x},${n.y})`}
                className={`gtw-node ${selectedId === n.id ? 'is-selected' : ''} ${linkFrom === n.id ? 'is-link-from' : ''}`}
                onClick={(ev) => onNodeClick(n.id, ev)}
                onPointerDown={(ev) => onPointerDownNode(n.id, ev)}
              >
                <circle r={22} />
                <text textAnchor="middle" dy="5">{n.label || '?'}</text>
              </g>
            ))}
          </svg>
          {selected ? (
            <div className="gtw-node-edit">
              <label>
                Имя узла
                <input
                  className="form-input"
                  value={selected.label || ''}
                  onChange={(e) => renameSelected(e.target.value)}
                  disabled={readOnly}
                />
              </label>
            </div>
          ) : null}
        </div>

        <div className="gtw-side">
          <div className="gtw-table-scroll">
            <table className="gtw-table">
              <thead>
                <tr>
                  <th />
                  {Array.from({ length: colCount }, (_, c) => (
                    <th key={c}>
                      <input
                        className="form-input gtw-header-input"
                        value={state.colLabels?.[c] || ''}
                        placeholder={`С${c + 1}`}
                        onChange={(e) => setColLabel(c, e.target.value)}
                        disabled={readOnly}
                        aria-label={`Столбец ${c + 1}`}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rowCount }, (_, r) => (
                  <tr key={r}>
                    <th>
                      <input
                        className="form-input gtw-header-input"
                        value={state.rowLabels?.[r] || ''}
                        placeholder={`Стр${r + 1}`}
                        onChange={(e) => setRowLabel(r, e.target.value)}
                        disabled={readOnly}
                        aria-label={`Строка ${r + 1}`}
                      />
                    </th>
                    {Array.from({ length: colCount }, (_, c) => (
                      <td key={c}>
                        <input
                          className="form-input gtw-cell-input"
                          value={state.cells?.[`${r},${c}`] ?? ''}
                          onChange={(e) => setCell(r, c, e.target.value)}
                          disabled={readOnly}
                          aria-label={`Ячейка ${r + 1},${c + 1}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(state.edges || []).length > 0 ? (
            <div className="gtw-edges">
              <div className="gtw-edges-title">Рёбра</div>
              <ul>
                {(state.edges || []).map((e) => {
                  const a = nodesById.get(e.from);
                  const b = nodesById.get(e.to);
                  return (
                    <li key={e.id}>
                      <span>{a?.label || '?'} → {b?.label || '?'}</span>
                      <input
                        className="form-input gtw-weight-input"
                        value={e.weight || ''}
                        placeholder="вес"
                        onChange={(ev) => setEdgeWeight(e.id, ev.target.value)}
                        disabled={readOnly}
                      />
                      <button type="button" className="btn btn-outline btn-xs" onClick={() => removeEdge(e.id)} disabled={readOnly}>
                        ×
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
